import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { v2URL } from 'src/app/shared/constants/urls.constants';
import { CommonService } from 'src/app/shared/services/common.service';

/**
 * Issue-type labels are configured per project (GET /v2/project/:id/issue-types),
 * but /search lists issues from many projects at once, and the projects store
 * only holds the labels of the project last opened. This resolves the label map
 * for any project on demand and caches it per project for the session, so the
 * search cards make at most one request per distinct project.
 */
@Injectable({ providedIn: 'root' })
export class IssueTypeLabelService {
  private readonly cache = new Map<string, Observable<Record<string, string>>>();

  constructor(private commonService: CommonService) {
  }

  labelsFor(projectId: string): Observable<Record<string, string>> {
    if (!projectId) {
      return of({});
    }

    if (!this.cache.has(projectId)) {
      const request$ = this.commonService
        .callAPI('get', v2URL.issueTypes.fetch.url({ projectId }))
        .pipe(
          map((response: any) => {
            const labels: Record<string, string> = {};
            (response?.data || []).forEach(item => {
              if (item?.type) {
                labels[item.type] = item.label;
              }
            });
            return labels;
          }),
          catchError(() => of({})),
          shareReplay(1),
        );

      this.cache.set(projectId, request$);
    }

    return this.cache.get(projectId);
  }
}

/** "project-coordination" → "Project coordination": fallback when no label is configured. */
export const humanizeIssueType = (type: any): string => {
  const text = typeof type === 'string' ? type.replace(/[-_]+/g, ' ').trim() : '';
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};
